import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography, CircularProgress, Grid } from '@mui/material';
import { useUploadHistoryStore } from '../../hooks/useUploadHistoryStore';
import MiniLineChart from './MiniLineChart';

const SummaryCards: React.FC = observer(() => {
  const store = useUploadHistoryStore();
  const { summary, dailyStats } = store;

  if (store.isLoading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return null;
  }

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatPercent = (num: number): string => {
    return `${(num * 100).toFixed(1)}%`;
  };

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Box
          sx={{
            bgcolor: 'custom.cardDark',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.75rem' }}>
              Total Files
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              {formatNumber(summary.total_files)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, opacity: 0.6, minWidth: 0 }}>
            <MiniLineChart
              data={dailyStats}
              dataKey="total_files"
              color="#4ade80"
              currentValue={summary.total_files}
            />
          </Box>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Box
          sx={{
            bgcolor: 'custom.cardDark',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.75rem' }}>
              Claims Found
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              {formatNumber(summary.total_claims_found)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, opacity: 0.6, minWidth: 0 }}>
            <MiniLineChart
              data={dailyStats}
              dataKey="total_claims_found"
              color="#a78bfa"
              currentValue={summary.total_claims_found}
            />
          </Box>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Box
          sx={{
            bgcolor: 'custom.cardDark',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.75rem' }}>
              Claims Processed
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              {formatNumber(summary.total_claims_processed)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, opacity: 0.6, minWidth: 0 }}>
            <MiniLineChart
              data={dailyStats}
              dataKey="total_claims_processed"
              color="#fbbf24"
              currentValue={summary.total_claims_processed}
            />
          </Box>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Box
          sx={{
            bgcolor: 'custom.cardDark',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.75rem' }}>
              Success Rate
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              {formatPercent(summary.success_rate)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, opacity: 0.6, minWidth: 0 }}>
            <MiniLineChart
              data={dailyStats}
              dataKey="success_rate"
              color="#fb923c"
              currentValue={summary.success_rate * 100}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
});

export default SummaryCards;

